import { Component, ElementRef, Input, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  Observable,
  catchError,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  map,
  of,
  switchMap,
} from 'rxjs';
import { CrmInjectionToken, SimplifiedUser } from '../../../tokens-and-interfaces';

interface OwnerOption {
  id: string;
  displayName: string;
}

type OnChangeFnType = (ownerId: string | undefined) => void;
type OnTouchedFnType = () => void;

@Component({
  selector: 'crm-form-field-activity-owner',
  templateUrl: './activity-owner.component.html',
  styleUrls: ['./activity-owner.component.scss'],
  imports: [CommonModule, GalaxyFormFieldModule, MatInputModule, ReactiveFormsModule, MatAutocompleteModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: CrmFormFieldActivityOwnerComponent,
    },
  ],
})
export class CrmFormFieldActivityOwnerComponent implements ControlValueAccessor {
  @Input() placeholder = '';
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  onChange?: OnChangeFnType;
  onTouched?: OnTouchedFnType;
  touched = false;
  disabled = false;

  private readonly config = inject(CrmInjectionToken);
  private readonly searchTerm$$ = new BehaviorSubject<string>('');
  readonly options$: Observable<OwnerOption[]>;
  ownerId?: string;
  searchCtrl = new FormControl();

  constructor() {
    this.options$ = this.searchTerm$$.pipe(
      takeUntilDestroyed(),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((searchTerm) => this.config.services?.userService?.searchUsers?.(searchTerm) ?? []),
      map((users) => this.mapSimplifiedUserToOwner(users)),
    );
    this.searchCtrl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => this.handleOptionSelected(value));
    this.validateSearchPermission();
  }

  writeValue(ownerId: string) {
    this.setCurrentValue(ownerId);
  }

  registerOnChange(onChange: OnChangeFnType) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: OnTouchedFnType) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched?.();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    if (this.disabled) {
      this.searchCtrl.disable();
    } else {
      this.searchCtrl.enable();
    }
  }

  displayFn(option?: OwnerOption): string {
    return option?.displayName || '';
  }

  private handleOptionSelected(option?: OwnerOption): void {
    if (this.searchCtrl.enabled) {
      this.ownerId = option?.id;
      this.onChange?.(this.ownerId);
      this.markAsTouched();
    }
  }

  handleSearchTermInput(): void {
    const searchTerm = this.searchInput?.nativeElement.value ?? '';
    this.searchTerm$$.next(searchTerm);
  }

  private mapSimplifiedUserToOwner(users: SimplifiedUser[]): OwnerOption[] {
    return users.map((u) => ({ id: u.userId, displayName: u.displayName }));
  }

  private setCurrentValue(ownerId: string): void {
    if (this.ownerId !== ownerId) {
      this.ownerId = ownerId;
      if (this.ownerId) {
        const fetch$ =
          this.config.services?.userService?.getMultiUsers?.([ownerId]).pipe(
            map((users) => users[0]),
            map((user) => user?.displayName || ''),
            catchError(() => of('')),
          ) ?? of('');
        const displayName = firstValueFrom(fetch$);
        displayName.then((displayName) => {
          if (this.searchInput?.nativeElement) {
            // without the timeout to force execution outside
            // the angular lifecycle
            // the value isn't properly set on first edit sometimes
            setTimeout(() => {
              this.searchInput!.nativeElement.value = displayName;
            }, 0);
          }
        });
      }
    }
  }

  private async validateSearchPermission(): Promise<void> {
    // disable the input if the searchUsers function is not available or the user don't have access to the results
    let validResult: Promise<void>;
    if (this.config.services?.userService?.searchUsers) {
      validResult = firstValueFrom(this.config.services.userService.searchUsers('')).then(() => undefined);
    } else {
      validResult = Promise.reject();
    }
    validResult.catch(() => this.searchCtrl.disable());
  }
}
